import React, { useEffect, useState } from "react";
import { Users, Building2, Briefcase, Send, Star, DollarSign, Loader2, Globe } from "lucide-react";
import { Layout } from "@/components/Layout";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { api } from "@/context/AuthContext";

const Stat = ({ icon: Icon, label, value }) => (
  <div className="gjt-card p-5">
    <Icon className="h-5 w-5 text-brand-blue" />
    <p className="mt-2 font-display text-2xl font-extrabold text-navy">{value ?? 0}</p>
    <p className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</p>
  </div>
);

export default function AdminDashboard() {
  const [o, setO] = useState(null);
  const [tab, setTab] = useState("candidates");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/admin/overview").then((r) => setO(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    setBusy(true);
    api.get(`/admin/${tab}`).then((r) => setRows(r.data.results)).catch(() => setRows([])).finally(() => setBusy(false));
  }, [tab]);

  if (!o) return <Layout><div className="flex justify-center py-32"><Loader2 className="h-6 w-6 animate-spin text-brand-blue" /></div></Layout>;

  const cols = {
    candidates: ["name", "city", "country", "availability", "sponsored"],
    employers: ["company_name", "city", "country", "free_credits"],
    jobs: ["title", "company_name", "city", "status", "payment_status"],
    applications: ["job_id", "status", "created_at"],
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <h1 className="font-display text-3xl font-extrabold text-navy">Admin Command Centre</h1>
        <p className="mt-1 text-sm text-slate-500">Marketplace overview · demo revenue is mocked.</p>

        <div className="mt-6 grid gap-4 grid-cols-2 lg:grid-cols-4">
          <Stat icon={Users} label="Candidates" value={o.candidates} />
          <Stat icon={Globe} label="Available now" value={o.available_now} />
          <Stat icon={Building2} label="Employers" value={o.employers} />
          <Stat icon={Briefcase} label="Active jobs" value={o.active_jobs} />
          <Stat icon={Send} label="Applications" value={o.applications} />
          <Stat icon={Star} label="Shortlists" value={o.shortlists} />
          <Stat icon={Send} label="Invitations" value={o.invitations} />
          <Stat icon={DollarSign} label="Mock revenue" value={`$${o.mock_revenue}`} />
        </div>

        <div className="mt-6 gjt-card p-3">
          <p className="px-2 py-1 text-xs text-slate-400">Countries: {(o.countries || []).join(", ")}</p>
        </div>

        <div className="mt-8">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList data-testid="admin-tabs">
              {["candidates", "employers", "jobs", "applications"].map((x) => (
                <TabsTrigger key={x} value={x} data-testid={`admin-tab-${x}`} className="capitalize">{x}</TabsTrigger>
              ))}
            </TabsList>
            {["candidates", "employers", "jobs", "applications"].map((x) => (
              <TabsContent key={x} value={x}>
                <div className="gjt-card mt-4 overflow-x-auto">
                  {busy ? <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-brand-blue" /></div> : (
                    <table className="w-full text-sm">
                      <thead><tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-400">
                        {cols[x].map((c) => <th key={c} className="px-4 py-3">{c.replace("_", " ")}</th>)}
                      </tr></thead>
                      <tbody>
                        {rows.slice(0, 100).map((r, i) => (
                          <tr key={r.id || i} data-testid={`admin-row-${x}-${i}`} className="border-b border-slate-50 hover:bg-slate-50">
                            {cols[x].map((c) => <td key={c} className="px-4 py-3 text-slate-600">{String(r[c] ?? "—").slice(0, 40)}</td>)}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  {!busy && rows.length === 0 && <p className="py-12 text-center text-sm text-slate-400">No records.</p>}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </div>
    </Layout>
  );
}
